import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  AlertBanner,
  BarChart,
  Button,
  ChartContainer,
  ChartTableView,
  DataTable,
  StatTile,
  StatusBadge,
  useLocale,
  type BarDatum,
  type DataTableColumn,
} from '@ministry/ui/internal';

import { useSession } from '../lib/session.js';
import { listFiles } from '../lib/files.js';
import { listMyTasks, type MyTaskRow } from '../lib/workflow.js';
import { listAppointments } from '../lib/appointments.js';
import { fileStageEntry, fileStageChartColor, FILTERABLE_STATUS_CODES, OVERDUE_FLAG } from '../lib/file-lifecycle.js';
import { roleLabel } from '../lib/roles.js';
import { ROUTES } from '../lib/nav.js';
import { useT } from '../i18n/strings.js';
import { PageHeader } from '../components/PageHeader.js';

interface DashboardLocationState {
  mustChangePassword?: boolean;
}

const TASK_PREVIEW_LIMIT = 5;
/** SLA "due soon" window — DESIGN_SYSTEM.md §1.4's own wording for `status-warning`: "SLA due soon". Not server-configurable anywhere in this build (no `/reference` endpoint for it, same gap `file-lifecycle.ts` already flags for confidentiality levels and reassignment reasons), so a fixed, honestly-documented 3-day window rather than an invented server value. */
const DUE_SOON_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

/**
 * SCREEN_SPECS.md I5 — the same URL for every role, content varying by what the SCOPED
 * queries return rather than by a client-side role branch (`GET /files`, `GET
 * /me/tasks`, `GET /appointments` all already resolve scope server-side).
 *
 * **Redesigned in this pass** (the user's own words: "not by any way looking like proper
 * dashboard"). The previous version showed exactly two stat tiles and a table — real
 * data, but not enough of it to answer "what needs me today, what is late, what is
 * coming" at a glance, which is what a section officer's day actually looks like. This
 * version adds:
 *   - Two more KPI tiles (due-soon, pending-appointment-decisions), each colored with
 *     the SAME reserved status token that badge/chart already uses for that exact
 *     meaning (`status-warning` for "SLA due soon", `status-info` for a "Pending"
 *     appointment) — `StatTile`'s `accent` union was extended in `packages/ui` to carry
 *     this, never a new invented hue.
 *   - A real chart: open files broken down by lifecycle stage, colored with the exact
 *     same status tokens `StatusBadge` uses, so "Forwarded" means the same color in the
 *     chart as it does on a file's own badge (DESIGN_SYSTEM.md §10.1's own instruction:
 *     "reusing the lifecycle vocabulary is the point").
 * Every number still traces to a real endpoint this build actually has — no reporting/
 * staffing/audit-summary endpoint exists (`internal-api`'s route table mounts only
 * `file`, `workflow`, `identity`, `notification`, `appointment`), so nothing here is
 * invented: the stage breakdown is `GET /files` called once per real status filter
 * (`totalItems` from each), and due-soon/pending-decisions are counts derived from
 * `GET /me/tasks` / `GET /appointments` data this screen (or a sibling screen) already
 * fetches in full, the same client-side-count-over-an-already-fetched-list pattern
 * `MyTasksPage` and `AppointmentInboxPage` already use for their own stat tiles.
 */
export function DashboardPage(): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const location = useLocation();
  const mustChangePassword = (location.state as DashboardLocationState | null)?.mustChangePassword;

  const canReadFiles = session?.permissions.includes('file.read') ?? false;
  /**
   * R-052, on the dashboard. A task is a file you currently HOLD, and holding one means
   * participating in movement — `file.move`, which the matrix denies outright to Super
   * Admin, Admin/HR and Auditor. Gating the task queue on `file.read` (which those three
   * DO hold, as administrative metadata) meant they fired `GET /me/tasks`, got `[]` every
   * time by construction, and were shown a permanently empty "My Tasks" table and a
   * due-soon tile frozen at zero. An empty state that can never fill is not reassuring —
   * it reads as broken, and it is the same defect that put a dead My Tasks item in the
   * nav. File tiles and the stage chart stay on `file.read`: those are real for them.
   */
  const canHoldFiles = session?.permissions.includes('file.move') ?? false;
  const canSeeAppointments = session?.permissions.includes('appointment.inbox') ?? false;

  const scopeCountQuery = useQuery({
    queryKey: ['dashboard-scope-count'],
    queryFn: ({ signal }) => listFiles({ page: 1, pageSize: 1 }, signal),
    enabled: canReadFiles,
  });
  const overdueCountQuery = useQuery({
    queryKey: ['dashboard-overdue-count'],
    queryFn: ({ signal }) => listFiles({ page: 1, pageSize: 1, isOverdue: true }, signal),
    enabled: canReadFiles,
  });
  const tasksQuery = useQuery({
    queryKey: ['dashboard-tasks-preview'],
    queryFn: ({ signal }) => listMyTasks({}, signal),
    enabled: canHoldFiles,
  });
  /**
   * `dateFrom`/`dateTo` on `GET /appointments` filter `requested_start`, NOT
   * `scheduled_start` (verified by reading `appointment-repository.ts`'s own SQL) — so an
   * "appointments today" tile built from that filter would silently misreport any
   * appointment that was RESCHEDULED onto or off today, since it would still filter by
   * the citizen's original request date. Rather than ship a tile that is quietly wrong
   * for exactly the appointments a reschedule touches, this dashboard only shows the
   * one appointment KPI the API can answer unambiguously: a status is a status,
   * regardless of which day it lands on. Flagged in the handback as an API gap, not
   * worked around.
   */
  const pendingAppointmentsQuery = useQuery({
    queryKey: ['dashboard-appointments-pending'],
    queryFn: ({ signal }) => listAppointments({ statusCode: 'pending', pageSize: 1 }, signal),
    enabled: canSeeAppointments,
  });

  // One real `GET /files?statusCode=...&pageSize=1` call per status this build actually
  // has (§10.1's own "Files ... by stage" report, adapted to this role's own scope since
  // no section-comparison endpoint exists) — `totalItems` per call, never a count
  // computed by paging through and summing rows client-side.
  const stageQueries = useQueries({
    queries: FILTERABLE_STATUS_CODES.map((statusCode) => ({
      queryKey: ['dashboard-stage-count', statusCode],
      queryFn: ({ signal }: { signal: AbortSignal }) => listFiles({ statusCode, pageSize: 1 }, signal),
      enabled: canReadFiles,
    })),
  });
  const stageLoading = stageQueries.some((q) => q.isLoading);
  const stageError = stageQueries.some((q) => q.isError);
  const stageCounts = FILTERABLE_STATUS_CODES.map((statusCode, i) => ({
    statusCode,
    count: stageQueries[i]?.data?.page.totalItems ?? 0,
  }));
  const stageTotal = stageCounts.reduce((sum, s) => sum + s.count, 0);

  if (!session) return <></>; // unreachable under RequireAuth, kept for type-narrowing only

  const taskRows = (tasksQuery.data ?? []).slice(0, TASK_PREVIEW_LIMIT);
  const anyTaskOverdue = taskRows.some((row) => row.isOverdue);

  const now = Date.now();
  const dueSoonCount = (tasksQuery.data ?? []).filter((row) => {
    if (row.isOverdue || row.dueAt === null) return false;
    const remaining = new Date(row.dueAt).getTime() - now;
    return remaining >= 0 && remaining <= DUE_SOON_WINDOW_MS;
  }).length;

  const allColumns: DataTableColumn<MyTaskRow>[] = [
    {
      key: 'trackingCode',
      header: t('myTasks.column.trackingCode'),
      visibleFrom: 'always',
      cardSlot: 'title',
      render: (row) => <span className="whitespace-nowrap font-mono">{row.trackingCode}</span>,
    },
    {
      key: 'statusCode',
      header: t('myTasks.column.status'),
      visibleFrom: 'always',
      cardSlot: 'status',
      render: (row) => <StatusBadge entry={fileStageEntry(row.statusCode)} />,
    },
    // Overdue is an orthogonal flag "appended to any [stage], not a stage of its own"
    // (DESIGN_SYSTEM.md §9.1). Included below only when at least one previewed task is
    // actually overdue — a dedicated column blank on every row was the named defect
    // ("one column renders entirely empty").
    {
      key: 'overdue',
      header: t('fileRegister.column.overdue'),
      visibleFrom: 'always',
      cardSlot: 'overdueBadge',
      render: (row) => (row.isOverdue ? <StatusBadge entry={OVERDUE_FLAG} variant="solid" /> : null),
    },
    {
      key: 'subject',
      header: t('myTasks.column.subject'),
      visibleFrom: 'always',
      cardSlot: 'line2',
      grow: true,
      render: (row) => row.subject,
    },
  ];

  const columns = anyTaskOverdue ? allColumns : allColumns.filter((c) => c.key !== 'overdue');

  const roleText = roleLabel(session.roles[0]?.roleCode, locale);

  const stageBarData: BarDatum[] = stageCounts.map(({ statusCode, count }) => {
    const entry = fileStageEntry(statusCode);
    return {
      label: locale === 'ne' ? entry.ne : entry.en,
      values: { count },
      color: fileStageChartColor(statusCode),
    };
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('dashboard.title')}
        description={t('dashboard.welcomeWithRole', { name: session.user.fullName, role: roleText })}
      />
      {/*
        A-043 closed: this used to be an inert banner ("contact Admin/HR — self-service
        password change is not available") with nothing to act on, because
        `AccountSettingsPage` had no password-change form at all. It does now — this
        banner is a real action, not a dead end, and `focusPassword` in the navigation
        state carries the user straight to (and focuses) that form.
      */}
      {mustChangePassword ? (
        <AlertBanner
          variant="warning"
          title={t('shell.mustChangePassword')}
          action={
            <Button size="sm" variant="secondary" onClick={() => navigate(ROUTES.account, { state: { focusPassword: true } })}>
              {t('shell.mustChangePassword.action')}
            </Button>
          }
        />
      ) : null}

      {!canReadFiles ? (
        <AlertBanner variant="info" title={t('dashboard.noFileAccess.title')}>
          {t('dashboard.noFileAccess.body')}
        </AlertBanner>
      ) : (
        <>
          {/* A row of stat tiles fills the available width proportionally instead of being
              squeezed into a small fixed-width box that leaves the rest of a wide screen
              empty (ux-review defect) — every tile also shares `StatTile`'s own `h-full`,
              so tiles never render at different heights. Each tile's accent is the SAME
              reserved status token that means the same thing everywhere else in this
              system (§1.4) — never an invented "dashboard-only" color. */}
          <div className="flex flex-wrap gap-3">
            <StatTile
              className="min-w-[220px] flex-1"
              label={t('dashboard.myFiles.title')}
              value={scopeCountQuery.data?.page.totalItems}
              loading={scopeCountQuery.isLoading}
              error={scopeCountQuery.isError}
              onRetry={() => scopeCountQuery.refetch()}
            />
            <StatTile
              className="min-w-[220px] flex-1"
              label={t('dashboard.overdue.title')}
              value={overdueCountQuery.data?.page.totalItems}
              loading={overdueCountQuery.isLoading}
              error={overdueCountQuery.isError}
              onRetry={() => overdueCountQuery.refetch()}
              accent={(overdueCountQuery.data?.page.totalItems ?? 0) > 0 ? 'serious' : 'default'}
            />
            {canHoldFiles ? (
              <StatTile
                className="min-w-[220px] flex-1"
                label={t('dashboard.dueSoon.title')}
                value={dueSoonCount}
                loading={tasksQuery.isLoading}
                error={tasksQuery.isError}
                onRetry={() => tasksQuery.refetch()}
                accent={dueSoonCount > 0 ? 'warning' : 'default'}
              />
            ) : null}
            {canSeeAppointments ? (
              <StatTile
                className="min-w-[220px] flex-1"
                label={t('dashboard.pendingAppointments.title')}
                value={pendingAppointmentsQuery.data?.page.totalItems}
                loading={pendingAppointmentsQuery.isLoading}
                error={pendingAppointmentsQuery.isError}
                onRetry={() => pendingAppointmentsQuery.refetch()}
                accent={(pendingAppointmentsQuery.data?.page.totalItems ?? 0) > 0 ? 'info' : 'default'}
              />
            ) : null}
          </div>

          <ChartContainer
            title={t('dashboard.stageChart.title')}
            headingLevel="h2"
            loading={stageLoading}
            error={stageError}
            empty={!stageLoading && !stageError && stageTotal === 0}
            onRetry={() => stageQueries.forEach((q) => q.refetch())}
            tableView={
              <ChartTableView
                caption={t('dashboard.stageChart.title')}
                columns={[t('dashboard.stageChart.statusColumn'), t('dashboard.stageChart.countColumn')]}
                rows={stageCounts.map(({ statusCode, count }) => {
                  const entry = fileStageEntry(statusCode);
                  return [locale === 'ne' ? entry.ne : entry.en, count];
                })}
              />
            }
          >
            <BarChart data={stageBarData} series={[{ key: 'count', label: t('dashboard.stageChart.countColumn') }]} mode="single" />
          </ChartContainer>

          {/* `DataTable` already supplies its own bordered/elevated container (every state,
              including loading/error/empty) — this section deliberately does NOT wrap it
              in a second `Card`, which would just nest one box inside another.

              R-052: the whole section is absent for a role denied `file.move`, rather
              than present-and-permanently-empty. Its "View all" button pointed at
              `/tasks`, which those roles no longer have in the nav either — a dashboard
              that links somewhere the menu says does not exist is worse than one that
              simply does not mention it. */}
          {canHoldFiles ? (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-lg font-semibold text-text-heading">{t('dashboard.tasksTitle')}</h2>
                <Button variant="ghost" size="sm" onClick={() => navigate(ROUTES.tasks)}>
                  {t('dashboard.tasksViewAll')}
                </Button>
              </div>
              <DataTable
                caption={t('dashboard.tasksTitle')}
                columns={columns}
                rows={taskRows}
                getRowId={(row) => row.id}
                loading={tasksQuery.isLoading}
                error={tasksQuery.isError}
                onRetry={() => tasksQuery.refetch()}
                onRowClick={(row) => navigate(ROUTES.fileDetail(row.id))}
                emptyTitle={t('dashboard.tasksEmpty.title')}
                emptyBody={t('dashboard.tasksEmpty.body')}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
